import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TemplatePicker from "components/admin/TemplatePicker";
import GameIcon from "components/admin/GameIcon";
import { formatNumber } from "utils/format";
import { optionSummary } from "layouts/templates/labels";
import { NumField, Choice } from "layouts/simulator/fields";
import GemSetDialog from "layouts/simulator/GemSetDialog";
import { ROLL_OPTIONS, emptyGear } from "layouts/simulator/build";

/** "1.800 công · 3.500 HP", bỏ chỉ số bằng 0. */
function statLine(stats) {
  if (!stats) return "-";
  const parts = [
    ["công", stats.atk],
    ["thủ", stats.def],
    ["HP", stats.hp],
    ["MP", stats.mp],
  ]
    .filter(([, v]) => v > 0)
    .map(([label, v]) => `${formatNumber(v)} ${label}`);
  return parts.length ? parts.join(" · ") : "-";
}

/**
 * Một ô trang bị: chọn món, chọn chỉ số gốc lấy ở đâu trong khoảng của mẫu, cấp cường hoá,
 * số sao dung hợp và ngọc ở từng lỗ. `result` là ô tương ứng trong kết quả máy chủ trả về,
 * dùng để hiện chỉ số cuối — con số đó do server tính, giao diện không tự nhân chia gì.
 */
function GearCard({ label, itemType, gear, result, meta, onChange, gemSlotCount, petStats }) {
  const [pickingSet, setPickingSet] = useState(false);
  const set = (patch) => onChange({ ...gear, ...patch });
  const maxEnchant = itemType === meta.wingType ? meta.maxEnchantWing : meta.maxEnchant;
  // Luật thật của game: chỉ trang bị lev cuối của chuỗi tiến hoá mới dung hợp được
  // (ItemTemplate.CanFusion). Chưa có kết quả từ máy chủ thì tạm cho 5 ô trang bị, rồi
  // máy chủ trả canFusion về là ô này tự ẩn đi.
  const isEquipSlot = itemType !== meta.wingType && itemType !== meta.skinType;
  const canFuse =
    result && !result.empty && result.canFusion !== undefined ? result.canFusion : isEquipSlot;
  const custom = gear.roll === "custom";
  const filled = Boolean(gear.templateId);
  // Dòng % và hiệu ứng trong trận của cánh / cải trang. Dùng lại đúng bộ giải mã của trang
  // "Sửa vật phẩm & chỉ số" để không có hai cách đọc cùng một mã dòng.
  const optionLines =
    result && !result.empty && result.itemOption
      ? optionSummary({
          type: result.type,
          itemOption: result.itemOption,
          itemOptionValue: result.itemOptionValue,
        }).filter(Boolean)
      : [];

  return (
    <MDBox
      p={2}
      borderRadius="lg"
      sx={({ palette, borders }) => ({
        border: `${borders.borderWidth[1]} solid ${palette.light.main}`,
        height: "100%",
      })}
    >
      <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
        <GameIcon path={result && !result.empty ? result.icon : null} size={32} fallback="shield" />
        <MDTypography variant="button" fontWeight="bold" flexGrow={1}>
          {label}
        </MDTypography>
        {filled && (
          <Tooltip title="Bỏ món này">
            <MDButton
              size="small"
              variant="text"
              color="error"
              iconOnly
              onClick={() => onChange(emptyGear())}
            >
              <Icon>close</Icon>
            </MDButton>
          </Tooltip>
        )}
      </MDBox>

      <TemplatePicker
        kind="item"
        size="small"
        label={petStats ? "Vật phẩm (pet đeo được)" : "Vật phẩm"}
        itemType={itemType}
        query={petStats}
        value={gear.templateId}
        onChange={(templateId) => set({ templateId })}
      />

      {filled && (
        <>
          <Grid container spacing={1} mt={1}>
            <Grid item xs={12}>
              <Choice
                label="Chỉ số gốc"
                value={gear.roll}
                options={ROLL_OPTIONS}
                onChange={(roll) => set({ roll })}
              />
            </Grid>
            {custom && (
              <>
                <Grid item xs={6}>
                  <NumField label="Công gốc" value={gear.atk} onChange={(atk) => set({ atk })} />
                </Grid>
                <Grid item xs={6}>
                  <NumField label="Thủ gốc" value={gear.def} onChange={(def) => set({ def })} />
                </Grid>
                <Grid item xs={6}>
                  <NumField label="HP gốc" value={gear.hp} onChange={(hp) => set({ hp })} />
                </Grid>
                <Grid item xs={6}>
                  <NumField label="MP gốc" value={gear.mp} onChange={(mp) => set({ mp })} />
                </Grid>
              </>
            )}
            <Grid item xs={canFuse ? 6 : 12}>
              <NumField
                label="Cường hoá"
                value={gear.lvl}
                min={0}
                max={maxEnchant}
                onChange={(lvl) => set({ lvl })}
                helper={`tối đa +${maxEnchant}`}
              />
            </Grid>
            {canFuse && (
              <Grid item xs={6}>
                <NumField
                  label="Dung hợp (sao)"
                  value={gear.fusion}
                  min={0}
                  max={meta.maxFusion}
                  onChange={(fusion) => set({ fusion })}
                  helper={`tối đa ★${meta.maxFusion}`}
                />
              </Grid>
            )}
          </Grid>

          {gemSlotCount > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                mb={1}
              >
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Ngọc khảm
                </MDTypography>
                <MDBox display="flex" gap={0.5}>
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={() => setPickingSet(true)}
                  >
                    <Icon>auto_awesome</Icon>&nbsp;Khảm bộ
                  </MDButton>
                  {gear.gems.some(Boolean) && (
                    <Tooltip title="Tháo hết ngọc">
                      <MDButton
                        size="small"
                        variant="text"
                        color="error"
                        iconOnly
                        onClick={() => set({ gems: [null, null, null] })}
                      >
                        <Icon>backspace</Icon>
                      </MDButton>
                    </Tooltip>
                  )}
                </MDBox>
              </MDBox>
              {Array.from({ length: gemSlotCount }).map((unused, slot) => {
                const gem = gear.gems[slot];
                const unlocked = gear.lvl >= meta.gemSlotUnlockLvl[slot];
                const shown = result && !result.empty ? result.gems[slot] : null;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <MDBox key={slot} mb={1}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={8}>
                        <TemplatePicker
                          kind="item"
                          size="small"
                          label={`Lỗ ${slot + 1}${unlocked ? "" : " (chưa mở)"}`}
                          itemType={meta.gemType}
                          value={gem ? gem.templateId : null}
                          onChange={(templateId) => {
                            const gems = [...gear.gems];
                            gems[slot] = templateId ? { templateId, lvl: gem ? gem.lvl : 0 } : null;
                            set({ gems });
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <NumField
                          label="Cấp"
                          disabled={!gem}
                          value={gem ? gem.lvl : 0}
                          min={0}
                          max={meta.maxGemLevel}
                          onChange={(lvl) => {
                            const gems = [...gear.gems];
                            gems[slot] = { ...gems[slot], lvl };
                            set({ gems });
                          }}
                        />
                      </Grid>
                    </Grid>
                    {shown && shown.templateId > 0 && (
                      <MDTypography variant="caption" color="text" display="block" mt={0.25}>
                        {`hệ ${shown.element} · bậc ${shown.tier}`}
                        {statLine(shown.stats) !== "-" ? ` · ${statLine(shown.stats)}` : ""}
                      </MDTypography>
                    )}
                  </MDBox>
                );
              })}
              {result && !result.empty && result.setId >= 0 && (
                <MDBox mt={1}>
                  <Chip
                    size="small"
                    color={result.setActive ? "success" : "default"}
                    variant={result.setActive ? "filled" : "outlined"}
                    label={`Bộ ${result.setName} ${result.setTier}${
                      result.setActive ? "" : " (trùng bộ với món khác, không kích hoạt)"
                    }`}
                  />
                  {result.setText && (
                    <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                      {result.setText}
                    </MDTypography>
                  )}
                </MDBox>
              )}
              {result && !result.empty && result.setId < 0 && gear.gems.some(Boolean) && (
                <MDTypography variant="caption" color="warning" display="block" mt={1}>
                  Ba viên này không tạo thành bộ nào — vẫn được cộng chỉ số phẳng, nhưng mất phần
                  hiệu ứng của bộ.
                </MDTypography>
              )}
            </>
          )}
        </>
      )}

      {result && !result.empty && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <MDTypography variant="caption" color="text" display="block">
            Gốc: {statLine(result.baseStats)}
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold" display="block">
            Sau cường hoá + dung hợp: {statLine(result.final)}
          </MDTypography>
          {optionLines.length > 0 && (
            <MDBox mt={0.75}>
              <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
                Dòng hiệu ứng
              </MDTypography>
              {optionLines.map((line) => (
                <MDTypography variant="caption" color="warning" display="block" key={line}>
                  {line}
                </MDTypography>
              ))}
            </MDBox>
          )}
          {(result.gemFlat.atk > 0 ||
            result.gemFlat.def > 0 ||
            result.gemFlat.hp > 0 ||
            result.gemFlat.mp > 0) && (
            <MDTypography variant="caption" color="info" display="block">
              Ngọc (cộng ở mức pet): {statLine(result.gemFlat)}
            </MDTypography>
          )}
          {isEquipSlot && !result.canFusion && (
            <MDTypography variant="caption" color="warning" display="block" mt={0.5}>
              Món này không dung hợp được — chỉ trang bị lev cuối của chuỗi tiến hoá mới dung hợp
              được.
            </MDTypography>
          )}
          <MDBox display="flex" gap={0.5} flexWrap="wrap" mt={1}>
            {!result.meets && (
              <Chip size="small" color="error" label="Pet không đủ chỉ số để mang" />
            )}
            {(result.require.str > 0 || result.require.agi > 0 || result.require.int > 0) && (
              <Chip
                size="small"
                variant="outlined"
                label={`Cần sức ${result.require.str} / nhanh ${result.require.agi} / trí ${result.require.int}`}
              />
            )}
          </MDBox>
        </>
      )}

      {pickingSet && (
        <GemSetDialog
          slotLabel={label}
          maxGemLevel={meta.maxGemLevel}
          onClose={() => setPickingSet(false)}
          onApply={(gems) => set({ gems })}
        />
      )}
    </MDBox>
  );
}

GearCard.defaultProps = { result: null, gemSlotCount: 0, petStats: null };

GearCard.propTypes = {
  label: PropTypes.string.isRequired,
  itemType: PropTypes.number.isRequired,
  gear: PropTypes.object.isRequired,
  result: PropTypes.object,
  meta: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  gemSlotCount: PropTypes.number,
  /** { petStr, petAgi, petInt } để chỉ chào món pet đủ chỉ số mang; null thì hiện tất cả. */
  petStats: PropTypes.object,
};

export default GearCard;
